import { useRouter } from '@abyss/web/hooks/useRouter';
import { Button } from '@abyss/web/ui/Button';
import { Flex } from '@abyss/web/ui/Flex';
import { IconBrand, ValidIconBrandName } from '@abyss/web/ui/IconBrand';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { TextCostIndicator } from '../../../common/CostIndicator/CostIndicator.styles';
import { useLagoon } from '../../../hooks/useLagoon';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { Constants, ReverseCoverageTypesCodes } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { ConstantsRoutes } from '../../ConstantsRoutes';
import { convertObjectToUrlParams } from '../../Utils';
import {
  AttributeLabel,
  AttributeValue,
  ComparisonGridContainer,
  ProviderAttributeContainer,
  ProviderCard,
  costIndicatorCss,
  ctaButtonStyles,
  descriptionTextCss,
  providerCardTitle,
} from './BHProviderComparisonGrid.styled';

type IconName = 'pill' | 'chat' | 'clock';

const ICON_MAP: Record<IconName, ValidIconBrandName> = {
  pill: 'pill',
  chat: 'chat',
  clock: 'clock',
};
export type BHProviderAttribute = {
  label: string;
  value: string;
  icon?: 'pill' | 'chat' | 'clock' | '';
};

export type BHProviderCard = {
  title: string;
  description: string;
  attributes: BHProviderAttribute[];
  ctaLabel: string;
  search: string;
};

const BH = ConstantsLagoon.UI_MESSAGING.BH_COMPARE_PROVIDER_TYPES;
const TKEY = 'BH_COMPARE_PROVIDER_TYPES';

type TFn = (key: string, options?: Record<string, unknown>) => string;

// Shared attribute rows: label + value now come from translations, not lagoon.
const ATTRIBUTE_ROWS: {
  labelKey: string;
  icon: IconName | '';
}[] = [
  { labelKey: 'PRESCRIBES_MEDICATION', icon: 'pill' },
  { labelKey: 'USES_TALK_THERAPY', icon: 'chat' },
  { labelKey: 'COST', icon: '' },
  { labelKey: 'WAIT_TIME', icon: 'clock' },
];

const CARD_CONFIG: {
  titleKey: string;
  valueKeys: [string, string, string, string];
  ctaNameKey?: string;
  searchKey: string;
}[] = [
  {
    titleKey: BH.BH_PROVIDER_TYPE_CARDS_THERAPIST,
    valueKeys: ['NO', 'YES', 'LOWEST', 'SHORTEST'],
    searchKey: 'THERAPIST',
  },
  {
    titleKey: BH.BH_PROVIDER_TYPE_CARDS_PSYCHOLOGIST,
    valueKeys: ['NO', 'SOMETIMES', 'HIGHER', 'LONGER'],
    searchKey: 'PSYCHOLOGIST',
  },
  {
    titleKey: BH.BH_PROVIDER_TYPE_CARDS_PSYCHIATRIC_NURSE,
    valueKeys: ['YES', 'NO', 'HIGHER', 'LONGER'],
    ctaNameKey: 'PSYCHIATRIC_NP',
    searchKey: 'ADVANCED_PSYCHIATRIC_NP',
  },
  {
    titleKey: BH.BH_PROVIDER_TYPE_CARDS_PSYCHIATRIC,
    valueKeys: ['YES', 'SOMETIMES', 'HIGHEST', 'LONGEST'],
    searchKey: 'PSYCHIATRIST',
  },
];

const getRow = (lagoonData: any[], key: string) =>
  find(lagoonData, { key }) ?? {};

const buildProviderCards = (lagoonData: any[], t: TFn): BHProviderCard[] =>
  CARD_CONFIG.map(({ titleKey, valueKeys, ctaNameKey, searchKey }) => {
    const row = getRow(lagoonData, titleKey);
    const title = row.title ?? '';
    const ctaName = ctaNameKey ? t(`${TKEY}.${ctaNameKey}`) : title;
    const search = t(`${TKEY}.${searchKey}`);

    return {
      title,
      description: row.message ?? '',
      attributes: ATTRIBUTE_ROWS.map(({ labelKey, icon }, index) => ({
        label: t(`${TKEY}.${labelKey}`),
        value: t(`${TKEY}.${valueKeys[index]}`),
        icon,
      })),
      ctaLabel: t(`${TKEY}.FIND_A_PROVIDER`, { providerType: ctaName }),
      search,
    };
  });

const ProviderAttribute = ({
  attribute,
}: {
  attribute: BHProviderAttribute;
}) => (
  <ProviderAttributeContainer>
    <AttributeLabel fontWeight={'$bold'}>{attribute.label}</AttributeLabel>
    <Flex alignItems="center">
      {attribute?.icon ? (
        <IconBrand
          icon={ICON_MAP[attribute.icon]}
          size={24}
          variant="onetone"
        />
      ) : (
        <TextCostIndicator color={'$primary1'} css={costIndicatorCss}>
          {'$'}
        </TextCostIndicator>
      )}
      <AttributeValue fontWeight={'$normal'}>{attribute.value}</AttributeValue>
    </Flex>
  </ProviderAttributeContainer>
);

const ProviderCardItem = ({
  card,
  index,
  onCTAClick,
}: {
  card: BHProviderCard;
  index: number;
  onCTAClick: (keyword: string) => void;
}) => (
  <ProviderCard
    data-testid={`bh-provider-card-${index}`}
    variant={index % 2 === 0 ? 'white' : 'gray'}
  >
    <Text css={providerCardTitle} fontWeight={'$bold'} size={'$lg'}>
      {card.title}
    </Text>
    <Text css={descriptionTextCss} fontWeight={'$normal'} size={'$sm'}>
      {card.description}
    </Text>
    {card?.attributes.map((attr) => (
      <ProviderAttribute attribute={attr} key={attr.label} />
    ))}
    <Button
      css={ctaButtonStyles}
      onClick={() => onCTAClick(card.search)}
      size="small"
      variant="outline"
    >
      {card.ctaLabel}
    </Button>
  </ProviderCard>
);

export const BHProviderComparisonGrid = ({
  onClose,
}: {
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const chipStore = useChipStore(
    useShallow((state: ChipState) => ({
      chipValue: state.chipValue,
      originalChipValue: state.originalChipValue,
      coverageType: state.coverageType,
      lob: state.lob,
      locale: state.locale,
      landedOnDetailsPageFromSearch: state.landedOnDetailsPageFromSearch,
    }))
  );
  const lagoonData = (useLagoon('ui-messaging') as () => any[])();
  const providerCards = buildProviderCards(lagoonData, t);

  const handleCTAClick = useCallback(
    (keyword: string) => {
      const urlParams = convertObjectToUrlParams(chipStore as ChipState, {
        search: keyword,
        searchType: Constants.SEARCH_TYPES.SPECIALTY,
        chipValue: keyword,
        coverageType: ReverseCoverageTypesCodes['BEHAVIORAL HEALTH'],
        [Constants.PSX_SOURCE]: `${Constants.SEARCH_SOURCE_TYPES.AUTO_COMPLETE}`,
      });
      navigate(`${ConstantsRoutes.PROVIDER_SEARCH_RESULTS.path}${urlParams}`);
      onClose?.();
    },
    [chipStore, navigate, onClose]
  );

  return (
    <ComparisonGridContainer data-testid="bh-provider-comparison-grid">
      {providerCards.map((card, index) => (
        <ProviderCardItem
          card={card}
          index={index}
          key={index}
          onCTAClick={handleCTAClick}
        />
      ))}
    </ComparisonGridContainer>
  );
};
